import {useEffect, useRef, useState} from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Pagination
} from "@mui/material";
import FeedElement from "../componenets/FeedElement";
import AddIcon from "@mui/icons-material/Add";
import {getCompanies} from "../services/CompanyService";
import {getFeed} from "../services/FeedService";
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FeedOnDemand from "../componenets/FeedOnDemand";
import Loader from "../componenets/Loader";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DateRangePickerComponent from "../componenets/DateRangePickerComponent";

const Feed = () => {

  const [items, setItems] = useState([])
  const [pagination, setPagination] = useState({
    page: 0,
    size: 8,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true
  });

  const [companies, setCompanies] = useState([]);

  const [company, setCompany] = useState("");

  const [type, setType] = useState("");

  const [refresh, setRefresh] = useState(false);

  const [onDemand, setOnDemand] = useState(false);

  const [loading, setLoading] = useState(false);

  const [isManual, setIsManual] = useState(null);

  const [isSeo, setIsSeo] = useState(false);

  const [sortField, setSortField] = useState({
    field: "CREATED_DATE", direction: true});

  const [sortDate, setSortDate] = useState(
      {startDate: null, endDate: null}
  );

  const dateRangeRef = useRef(null);

  useEffect(() => {
    getCompanies()
        .then(res => setCompanies(res.data))
  }, []);

  const resetDateRange = () => {
    if (dateRangeRef.current) {
      dateRangeRef.current.reset();
    }
  };

  const handleSortField = (fieldValue) => {
    setSortField(prv => ({
      field: fieldValue,
      direction: prv ? !prv.direction : true
    }));
    // Reset to first page when sorting changes
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage - 1 })); // MUI Pagination is 1-based, backend is 0-based
  };

  useEffect(() => {
    setLoading(true)
    getFeed({
      companyId: company?.id,
      type,
      isManual,
      isSeo,
      sortField: sortField.field,
      fromScheduledDate: sortDate.startDate,
      toScheduledDate: sortDate.endDate,
      direction: sortField.direction ? "DESC" : "ASC",
      page: pagination.page,
      size: pagination.size
    })
        .then(res => {
          setItems(res.data.content)
          setPagination({
            page: res.data.page,
            size: res.data.size,
            totalElements: res.data.totalElements,
            totalPages: res.data.totalPages,
            first: res.data.first,
            last: res.data.last
          });
        })
        .finally(() => setLoading(false))
  }, [company, type, isManual, refresh, onDemand, isSeo, sortField, sortDate, pagination.page, pagination.size]);

  const clearAllFilters = () => {
    setCompany("");
    setType("");
    setIsManual(null);
    setIsSeo(false);
    resetDateRange();
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const hasActiveFilters = company || type || isManual !== null || isSeo || sortDate.startDate || sortDate.endDate;

  return (
      <>
        <Loader loading={loading}/>
        <FeedOnDemand open={onDemand} handleClose={() => setOnDemand(false)}/>

        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, mb: 3 }}>
          <Typography sx={{
            fontWeight: 700,
            fontSize: { md: "32px", xs: "24px" },
            lineHeight: { md: "40px", xs: "32px" }
          }} color="primary.main">
            Feed
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setRefresh(prv => !prv);
                  setPagination(prev => ({ ...prev, page: 0 }));
                }}
            >
              Refresh
            </Button>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOnDemand(true)}
            >
              Create Feed
            </Button>
          </Box>
        </Box>

        {/* Filters Section */}
        <Paper sx={{ p: 2, mb: 3 }} elevation={1}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem" }}>
            Filters
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
            {/* Company Filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Company</InputLabel>
              <Select
                  label="Company"
                  value={company}
                  onChange={(e) => {
                    setCompany(e.target.value);
                    setPagination(prev => ({ ...prev, page: 0 }));
                  }}
              >
                <MenuItem value="">
                  <em>All Companies</em>
                </MenuItem>
                {companies.map((comp, i) => (
                    <MenuItem key={i} value={comp}>{comp.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Type Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Type</InputLabel>
              <Select
                  label="Type"
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    setPagination(prev => ({ ...prev, page: 0 }));
                  }}
              >
                <MenuItem value="">
                  <em>All Types</em>
                </MenuItem>
                <MenuItem value="NEWS">News</MenuItem>
                <MenuItem value="WEEKLY_DIGEST">Weekly Digest</MenuItem>
                <MenuItem value="NEWS_GENERAL">General News</MenuItem>
                <MenuItem value="NEWS_FINANCE">Finance News</MenuItem>
                <MenuItem value="SINGLE_POST">Single Post</MenuItem>
                <MenuItem value="ARTICLE">Article</MenuItem>
                <MenuItem value="BANNER">Branding Banner</MenuItem>
              </Select>
            </FormControl>

            {/* Quick Filter Chips */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                  label="Manual"
                  variant={isManual === true ? "filled" : "outlined"}
                  color={isManual === true ? "primary" : "default"}
                  onClick={() => {
                    setIsManual(prv => prv === true ? null : true);
                    setPagination(prev => ({ ...prev, page: 0 }));
                  }}
                  sx={{ cursor: "pointer" }}
              />
              <Chip
                  label="SEO"
                  variant={isSeo ? "filled" : "outlined"}
                  color={isSeo ? "primary" : "default"}
                  onClick={() => {
                    setIsSeo(prv => !prv);
                    setPagination(prev => ({ ...prev, page: 0 }));
                  }}
                  sx={{ cursor: "pointer" }}
              />
            </Box>

            {/* Date Range */}
            <DateRangePickerComponent ref={dateRangeRef} onChange={(res) => {
              setSortDate(res);
              setPagination(prev => ({ ...prev, page: 0 }));
            }}/>

            {/* Clear Filters */}
            {hasActiveFilters && (
                <Tooltip title="Clear all filters">
                  <IconButton
                      onClick={clearAllFilters}
                      color="error"
                      size="small"
                  >
                    <FilterAltOffIcon />
                  </IconButton>
                </Tooltip>
            )}
          </Box>
        </Paper>
        {/* Feed Results Section */}
        <Paper sx={{ p: 2 }} elevation={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem" }}>
              Feed Results
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pagination.totalElements} total entries
            </Typography>
          </Box>
        <Table sx={{tableLayout: "fixed", width: "100%", "&.MuiTable-root" : {
            borderCollapse: "separate",
            borderSpacing: "0 8px",
          }}}>
          <TableHead>
            <TableRow sx={{ "& .MuiTableCell-root": { padding: "6px 6px", fontSize: "0.75rem", fontWeight: 600 } }}>
              <TableCell align={"center"} sx={{ width: "7%" }}>
                <Box onClick={() => handleSortField("CREATED_DATE")} style={{display: "flex", justifyContent: "center", alignItems: "center"}} >
                  Created
                  <Box style={{cursor: "pointer", display: "flex", alignItems: "center"}}>
                    {sortField.field === "CREATED_DATE" && sortField.direction ? <KeyboardArrowDownIcon fontSize="small"/> : <KeyboardArrowUpIcon fontSize="small"/>}
                  </Box>
                </Box>
              </TableCell>
              <TableCell align={"center"} sx={{ width: "8%" }}>
                Company
              </TableCell>
              <TableCell align={"center"} sx={{ width: "6%" }}>
                Type
              </TableCell>
              <TableCell align={"left"} sx={{ width: "14%" }}>
                Title
              </TableCell>
              <TableCell align={"left"} sx={{ width: "12%" }}>
                Format
              </TableCell>
              <TableCell align={"left"} sx={{ width: "12%" }}>
                Subformat
              </TableCell>
              <TableCell align={"center"} sx={{ width: "6%" }}>
                Status
              </TableCell>
              <TableCell align={"center"} sx={{ width: "5%" }}>
                Manual
              </TableCell>
              <TableCell align={"center"} sx={{ width: "8%" }}>
                <Box onClick={() => handleSortField("SCHEDULED_DATE")} style={{display: "flex", justifyContent: "center", alignItems: "center"}} >
                  Scheduled
                  <Box style={{cursor: "pointer", display: "flex", alignItems: "center"}}>
                    {sortField.field === "SCHEDULED_DATE" && sortField.direction ? <KeyboardArrowDownIcon fontSize="small"/> : <KeyboardArrowUpIcon fontSize="small"/>}
                  </Box>
                </Box>
              </TableCell>
              <TableCell align={"center"} sx={{ width: "8%" }}>
                <Box onClick={() => handleSortField("REVIEW_DATE")} style={{display: "flex", justifyContent: "center", alignItems: "center"}} >
                  Review
                  <Box style={{cursor: "pointer", display: "flex", alignItems: "center"}}>
                    {sortField.field === "REVIEW_DATE" && sortField.direction ? <KeyboardArrowDownIcon fontSize="small"/> : <KeyboardArrowUpIcon fontSize="small"/>}
                  </Box>
                </Box>
              </TableCell>
              <TableCell align={"center"} sx={{ width: "8%" }}>
                Posted
              </TableCell>
              <TableCell align={"center"} sx={{ width: "5%" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items?.map(function(item, i){
              return <FeedElement setReload={() => setRefresh(prv => !prv)} item={item} key={i} />;
            })}
          </TableBody>
        </Table>

        {/* Pagination Component */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
          <Pagination
              count={pagination.totalPages}
              page={pagination.page + 1} // MUI Pagination is 1-based
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
          />
        </Box>

        {/* Pagination Info */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {pagination.page * pagination.size + 1} to {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of {pagination.totalElements} entries
          </Typography>
        </Box>
        </Paper>
      </>
  )
};

export default Feed;
